import MuiAutocompleteField, {
  type AutocompleteProps,
} from '@mui/material/Autocomplete';
import type { InputLabelProps } from '@mui/material/InputLabel';
import { forwardRef, type HTMLAttributes } from 'react';
import TextField, { type InputProps, type TextFieldProps } from './TextInput';

export type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

export type AutocompleteOption = Record<string, any>;

type BaseAutocompleteInputProps = {
  error?: boolean;
  required?: boolean;
  margin?: TextFieldProps['margin'];
  helperText?: React.ReactNode;
  inputSlotProps?: InputProps;
  inputLabelSlotProps?: InputLabelProps;
  label?: React.ReactNode;
  disabled?: boolean;
  sx?: AutocompleteProps<AutocompleteOption, false, false, false>['sx'];
  valuePropName?: string;
  placeholder: string;
};

type SingleAutocompleteInputProps<T extends AutocompleteOption> = Omit<
  AutocompleteProps<T, false, false, false>,
  'renderInput' | 'value' | 'options' | 'onChange'
> &
  BaseAutocompleteInputProps & {
    multiple?: false;
    value?: T | null;
    options: T[];
    onChange: (value: T | null) => void;
  };

type MultipleAutocompleteInputProps<T extends AutocompleteOption> = Omit<
  AutocompleteProps<T, true, false, false>,
  'renderInput' | 'value' | 'options' | 'onChange'
> &
  BaseAutocompleteInputProps & {
    multiple: true;
    value?: T[];
    options: T[];
    onChange: (value: T[]) => void;
  };

type AutocompleteInputProps<T extends AutocompleteOption = AutocompleteOption> =
  | SingleAutocompleteInputProps<T>
  | MultipleAutocompleteInputProps<T>;

function dedupeOptionsByValue<T extends AutocompleteOption>(
  options: T[],
  valuePropName: string,
): T[] {
  const seenValues = new Set<string | number>();
  return options.filter((option) => {
    if (seenValues.has(option[valuePropName])) {
      return false;
    }
    seenValues.add(option[valuePropName]);
    return true;
  });
}

function AsyncAutocompleteInputInner<T extends AutocompleteOption>(
  props: AutocompleteInputProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  if (props.multiple) {
    const {
      value = [],
      options,
      onChange,
      valuePropName = 'id',
      isOptionEqualToValue = (option, selectedValue) =>
        option.id === selectedValue.id,
      getOptionLabel = (option) => option.label ?? option,
      renderOption,
      error,
      helperText,
      inputSlotProps,
      inputLabelSlotProps,
      label,
      placeholder,
      required,
      margin,
      disabled,
      sx,
      ...autocompleteProps
    } = props;
    const normalizedOptions = dedupeOptionsByValue(
      [...options, ...value],
      valuePropName,
    );
    const defaultRenderOption: NonNullable<
      MultipleAutocompleteInputProps<T>['renderOption']
    > = (
      optionProps: HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: T,
    ) => {
      const { key, ...restOptionProps } = optionProps;
      return (
        <li key={key} {...restOptionProps}>
          {getOptionLabel(option)}
        </li>
      );
    };

    return (
      <MuiAutocompleteField<T, true, false, false>
        ref={ref}
        value={value as T[]}
        options={normalizedOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={(_: React.SyntheticEvent, selectedOptions: T[]) => {
          onChange(dedupeOptionsByValue(selectedOptions, valuePropName));
        }}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption ?? defaultRenderOption}
        fullWidth
        disabled={disabled}
        sx={sx}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            label={label}
            required={required}
            margin={margin}
            disabled={disabled}
            helperText={helperText}
            error={error}
            slotProps={{
              ...params.slotProps,
              htmlInput: params.slotProps.htmlInput,
              input: { ...params.slotProps.input, ...inputSlotProps },
              inputLabel: {
                ...params.slotProps.inputLabel,
                ...inputLabelSlotProps,
              },
            }}
          />
        )}
        {...autocompleteProps}
      />
    );
  }

  const {
    value = null,
    options,
    onChange,
    valuePropName = 'id',
    isOptionEqualToValue = (option, selectedValue) =>
      option.id === selectedValue.id,
    getOptionLabel = (option) => option.label ?? option,
    renderOption,
    error,
    helperText,
    inputSlotProps,
    inputLabelSlotProps,
    label,
    placeholder,
    required,
    margin,
    disabled,
    sx,
    ...autocompleteProps
  } = props;
  const normalizedOptions = value
    ? dedupeOptionsByValue([...options, value], valuePropName)
    : options;
  const defaultRenderOption: NonNullable<
    SingleAutocompleteInputProps<T>['renderOption']
  > = (
    optionProps: HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: T,
  ) => {
    const { key, ...restOptionProps } = optionProps;
    return (
      <li key={key} {...restOptionProps}>
        {getOptionLabel(option)}
      </li>
    );
  };

  return (
    <MuiAutocompleteField<T, false, false, false>
      ref={ref}
      value={value}
      options={normalizedOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(_: React.SyntheticEvent, selectedOption: T | null) => {
        onChange(selectedOption);
      }}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption ?? defaultRenderOption}
      fullWidth
      disabled={disabled}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          label={label}
          required={required}
          margin={margin}
          disabled={disabled}
          helperText={helperText}
          error={error}
          slotProps={{
            ...params.slotProps,
            htmlInput: params.slotProps.htmlInput,
            input: { ...params.slotProps.input, ...inputSlotProps },
            inputLabel: {
              ...params.slotProps.inputLabel,
              ...inputLabelSlotProps,
            },
          }}
        />
      )}
      {...autocompleteProps}
    />
  );
}

const AsyncAutocompleteInput = forwardRef(AsyncAutocompleteInputInner) as <
  T extends AutocompleteOption = AutocompleteOption,
>(
  props: AutocompleteInputProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

export default AsyncAutocompleteInput;
